import React, { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserPermission from "../../interfaces/security/user-permission";
import AuthService from "../../services/autentication/auth.service";
import TokenService from "../../services/autentication/token.service";
import SecurityService from "../../services/security/security.service";
import UserService from "../../services/user/user.service";
import Helpers from "../../utils/helpers";

export interface PermissionsContextType {
  canAccessViewSiteUpload?: boolean;
  canAccessEditSiteUpload?: boolean;
  canAccessViewSiteSurvey?: boolean;
  canAccessEditSiteSurvey?: boolean;
  canAccessViewSuveyDetails?: boolean;
  canAccessEditSuveyDetails?: boolean;
  canAccessViewAllSurveyDashboard?: boolean;
  globalAdmin?: boolean;
  readOnlyGlobalAdmin?: boolean;
  customerAdmin?: boolean;
}

export const PermissionsContext = createContext<PermissionsContextType>(null!);

export const PermissionsProvider = (props: any) => {
  const [canAccessViewSiteUpload, setCanAccessViewSiteUpload] = useState(false);
  const [canAccessEditSiteUpload, setCanAccessEditSiteUpload] = useState(false);
  const [canAccessViewSiteSurvey, setCanAccessViewSiteSurvey] = useState(false);
  const [canAccessEditSiteSurvey, setCanAccessEditSiteSurvey] = useState(false);
  const [canAccessViewSuveyDetails, setCanAccessViewSuveyDetails] =
    useState(false);
  const [canAccessEditSuveyDetails, setCanAccessEditSuveyDetails] =
    useState(false);
  const [canAccessViewAllSurveyDashboard, setCanAccessViewAllSurveyDashboard] =
    useState(false);

  const [globalAdmin, setGlobalAdmin] = useState(false);
  const [readOnlyGlobalAdmin, setReadOnlyGlobalAdmin] = useState(false);
  const [customerAdmin, setCustomerAdmin] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Strip OAuth `at` from the URL whenever it appears (including client-side navigations).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("at")) return;
    const at = params.get("at");
    if (!at) return;
    TokenService.bootstrapAccessTokenFromUrl();
    params.delete("at");
    const qs = params.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : "" },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  // Bootstrap + refresh once on mount, then load profile/permissions.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      TokenService.bootstrapAccessTokenFromUrl();
      await AuthService.refreshTokenFromServer(true);

      if (cancelled) return;

      if (TokenService.getLocalAccessToken() === null || TokenService.getLocalAccessToken() === "") {
        window.location.href =
          process.env.REACT_APP_LOGOUT_URL ||
          "https://cnx-legacy-enterprise-portal-qa.azurewebsites.net/Account/Login?Logout=1";
        return;
      }

      UserService.getCurrentUser()
        .then((userDetails: any) => {
          if (cancelled) return;
          Helpers.ConsoleLog(userDetails);
          void getPermissionOfCurrentUser();
        })
        .catch((err: unknown) => {
          Helpers.ConsoleLogError(err);
        });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timerId = setInterval(() => {
      AuthService.refreshTokenFromServer(false);
    }, 1740000); // refreshing token before it expires
    // Allow Jest workers to exit: long intervals otherwise keep the Node event loop alive.
    const maybeTimeout = timerId as unknown as { unref?: () => void };
    maybeTimeout.unref?.();
    return () => {
      clearInterval(timerId);
    };
  }, []);

  const getPermissionOfCurrentUser = async () => {
    var userPermissions: UserPermission =
      await SecurityService.getPermissionsOfCurrentUser();
    setCanAccessViewSiteUpload(userPermissions.ViewSiteUpload);
    setCanAccessEditSiteUpload(userPermissions.EditSiteUpload);
    setCanAccessViewSiteSurvey(userPermissions.ViewSiteSurvey);
    setCanAccessEditSiteSurvey(userPermissions.EditSiteSurvey);
    setCanAccessViewSuveyDetails(userPermissions.ViewSuveyDetails);
    setCanAccessEditSuveyDetails(userPermissions.EditSuveyDetails);
    setCanAccessViewAllSurveyDashboard(userPermissions.ViewAllSurveyDashboard);
    setGlobalAdmin(userPermissions.GlobalAdmin);
    setReadOnlyGlobalAdmin(userPermissions.ReadOnlyGlobalAdmin);
    setCustomerAdmin(userPermissions.CustomerAdmin);
  };

  return (
    <PermissionsContext.Provider
      value={{
        canAccessViewSiteUpload,
        canAccessEditSiteUpload,
        canAccessViewSiteSurvey,
        canAccessEditSiteSurvey,
        canAccessViewSuveyDetails,
        canAccessEditSuveyDetails,
        canAccessViewAllSurveyDashboard,
        globalAdmin,
        readOnlyGlobalAdmin,
        customerAdmin
      }}
    >
      {props.children}
    </PermissionsContext.Provider>
  );
};
